import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import {
  BUTTON_ACTIONS,
  FORMAT_FOR_DATES_LONG,
} from 'src/app/core/helpers/ui/ui.constant';
import { BonusPromotionalCode } from 'src/app/core/interfaces/api/report.interface';
import {
  PlayerPromotionalCodeActor,
  PlayerPromotionalCodeActors,
} from 'src/app/core/interfaces/api/promotional-tools.interface';
import { ModalWithAction } from 'src/app/core/interfaces/ui/bootstrap-modal.interface';
import { ButtonAction } from 'src/app/core/interfaces/ui/ui.interface';
import { PromotionalCodeService } from 'src/app/core/services/api/promotional-code.service';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { LanguageService } from 'src/app/core/services/ui/language.service';

@Component({
  selector: 'report-bonus-report-info-modal',
  templateUrl: './bonus-report-info-modal.component.html',
})
export class BonusReportInfoModalComponent {
  public titleModal: string = '';
  public bonusReportInfo: BonusPromotionalCode | undefined = undefined;
  public actors: PlayerPromotionalCodeActors | null = null;
  public actorsLoading: boolean = false;
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();
  private actorsRequest?: Subscription;
  public FORMAT_FOR_DATES_LONG = FORMAT_FOR_DATES_LONG;

  constructor(
    private _bsModalService: BootstrapModalService<
      ModalWithAction<BonusPromotionalCode>
    >,
    public _lang: LanguageService,
    private _promotionalCodeService: PromotionalCodeService,
    private _translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.suscribeModalDataIssued();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  private suscribeModalDataIssued(): void {
    this._bsModalService
      .getDataIssued()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ buttonAction, selectedRow }) =>
          this.processSelectedUser(buttonAction, selectedRow),
        error: () => this.closeModal(),
      });
  }

  private processSelectedUser(
    buttonAction: ButtonAction,
    bonusInfo?: BonusPromotionalCode
  ): void {
    this.bonusReportInfo = bonusInfo;
    // A new row must never show the previous row's actors.
    this.actorsRequest?.unsubscribe();
    this.actors = null;

    const executeAction = this.handleProcessButtonAction(buttonAction);
    executeAction();
  }

  private handleProcessButtonAction(buttonAction: ButtonAction): () => void {
    const actionMap = new Map<ButtonAction, () => void>([
      [BUTTON_ACTIONS.VIEW, () => this.handleViewUser()],
    ]);
    return actionMap.get(buttonAction) || (() => {});
  }

  private handleViewUser(): void {
    this.titleModal = 'reports.bonus.title';
    this.loadActors();
  }

  // Actors are not part of the table rows (too heavy to join in the paginated
  // report), so they are fetched only when the detail is opened. Any failure
  // leaves them empty ("-") without affecting the rest of the modal.
  // A previous, slower request is cancelled so its response cannot overwrite
  // the actors of the row now on screen.
  private loadActors(): void {
    this.actorsRequest?.unsubscribe();
    this.actors = null;
    const playerPromotionalCodeId = this.bonusReportInfo?._id;
    if (!playerPromotionalCodeId) return;

    this.actorsLoading = true;
    this.actorsRequest = this._promotionalCodeService
      .getPlayerPromotionalCodeActors(playerPromotionalCodeId)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => (this.actorsLoading = false))
      )
      .subscribe({
        next: ({ data }) => (this.actors = data ?? null),
        error: () => (this.actors = null),
      });
  }

  public getActorLabel(actor?: PlayerPromotionalCodeActor | null): string {
    if (!actor) return '-';
    if (actor.type === 'system') return this._translateService.instant('reports.bonus.systemActor');
    return actor.label?.trim() || '-';
  }

  public closeModal(): void {
    this._bsModalService.closeModal();
  }
}
